import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Compass, X } from "lucide-react";
import "driver.js/dist/driver.css";
import "@/lib/productTour.css";
import { Button } from "@/components/ui/button";
import { runProductTour } from "@/lib/runProductTour";
import { ADMIN_TOUR_GROUPS, EMPLOYEE_TOUR_GROUPS } from "@/lib/productTourSteps";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { PRODUCT_TOUR } from "@/constants/testIds";

// Globo flotante fijo para activar el tour guiado interactivo en cualquier momento, en
// AdminLayout y EmployeeLayout. La primera vez que este usuario inicia sesión (bandera
// `tour_invite_shown` en el backend, no local), se muestra además una invitación breve y
// descartable — pase lo que pase con esa invitación, nunca vuelve a aparecer sola una segunda
// vez, pero este botón nunca desaparece.
export function ProductTourFab({ role }) {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [inviteVisible, setInviteVisible] = useState(false);
  const invitedRef = useRef(false);

  const groups = role === "admin" ? ADMIN_TOUR_GROUPS : EMPLOYEE_TOUR_GROUPS;

  useEffect(() => {
    if (!user || user.tour_invite_shown || invitedRef.current) return;
    invitedRef.current = true;
    const timer = setTimeout(() => {
      setInviteVisible(true);
      // Se marca como "ya mostrada" en cuanto aparece, no solo al responderla — así, aunque
      // la persona se vaya a otra pantalla sin hacer clic en nada, no vuelve a aparecer sola.
      api.post("/auth/tour-invite-shown").catch(() => {});
      setUser((prev) => (prev ? { ...prev, tour_invite_shown: true } : prev));
    }, 900);
    return () => clearTimeout(timer);
  }, [user, setUser]);

  const startTour = () => {
    setInviteVisible(false);
    runProductTour(groups, navigate);
  };

  const dismissInvite = () => setInviteVisible(false);

  const isEmployee = role === "employee";

  return (
    <div className={isEmployee ? "fixed inset-x-0 bottom-0 max-w-md mx-auto pointer-events-none" : ""}>
      <div
        className={`pointer-events-auto z-40 flex flex-col items-end gap-3 ${
          isEmployee ? "absolute bottom-44 right-5" : "fixed bottom-28 right-8"
        }`}
      >
        {inviteVisible && (
          <div
            data-testid={PRODUCT_TOUR.inviteCard}
            className="relative w-64 bg-card border border-petroleum/15 rounded-2xl p-4 shadow-[0_8px_30px_rgba(14,79,79,0.2)]"
          >
            <button
              data-testid={PRODUCT_TOUR.inviteCloseButton}
              onClick={dismissInvite}
              className="absolute top-2.5 right-2.5 text-charcoal/30 hover:text-charcoal/60"
              aria-label="Cerrar"
            >
              <X className="w-3.5 h-3.5" />
            </button>
            <p className="text-sm font-semibold text-charcoal pr-4">¿Quieres un recorrido rápido?</p>
            <p className="text-xs text-charcoal/60 mt-1">Te mostramos, paso a paso, dónde está todo.</p>
            <div className="flex gap-2 mt-3">
              <Button data-testid={PRODUCT_TOUR.inviteYesButton} size="sm" onClick={startTour} className="flex-1 bg-terracotta hover:bg-terracotta/90 rounded-full text-xs h-8">
                Sí
              </Button>
              <Button data-testid={PRODUCT_TOUR.inviteNoButton} size="sm" variant="outline" onClick={dismissInvite} className="flex-1 rounded-full text-xs h-8">
                Ahora no
              </Button>
            </div>
          </div>
        )}

        <button
          data-testid={PRODUCT_TOUR.fabButton}
          onClick={startTour}
          className="w-12 h-12 rounded-full bg-petroleum text-white shadow-lg shadow-petroleum/30 flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
          aria-label="Iniciar recorrido guiado"
        >
          <Compass className="w-5 h-5" strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}
